import { createClient } from '@/lib/supabase/client'
import type { PaymentCard } from '@/lib/types'

/**
 * Fetch all payment cards for a user
 */
export async function fetchUserCards(userId: string, subAccountId?: string | null): Promise<PaymentCard[]> {
  const supabase = createClient()

  let query: any = supabase
    .from('user_payment_cards')
    .select('*')
    .eq('user_id', userId)

  // Filter by sub-account if specified
  if (subAccountId !== undefined) {
    if (subAccountId === null) {
      query = query.is('sub_account_id', null)
    } else {
      query = query.eq('sub_account_id', subAccountId)
    }
  }

  const { data, error } = await query.order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching payment cards:', error)
    throw new Error('Failed to fetch payment cards')
  }

  // Transform database records to application types
  return data.map((card) => ({
    id: card.id,
    nickname: card.nickname,
    cardType: card.card_type as 'Visa' | 'Mastercard' | 'Amex' | 'Discover' | 'Other',
    lastFour: card.last_four,
    icon: card.icon,
    color: card.color,
    subAccountId: (card as any).sub_account_id,
  }))
}

/**
 * Create a new payment card
 */
export async function createCard(
  userId: string,
  card: Omit<PaymentCard, 'id'>
): Promise<PaymentCard> {
  const supabase = createClient()

  const { data, error } = await supabase
    .from('user_payment_cards')
    .insert({
      user_id: userId,
      nickname: card.nickname,
      card_type: card.cardType,
      last_four: card.lastFour,
      icon: card.icon,
      color: card.color,
      sub_account_id: card.subAccountId || null,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating payment card:', error)
    throw new Error('Failed to create payment card')
  }

  return {
    id: data.id,
    nickname: data.nickname,
    cardType: data.card_type as 'Visa' | 'Mastercard' | 'Amex' | 'Discover' | 'Other',
    lastFour: data.last_four,
    icon: data.icon,
    color: data.color,
    subAccountId: (data as any).sub_account_id,
  }
}

/**
 * Update an existing payment card
 */
export async function updateCard(
  cardId: string,
  updates: Partial<Omit<PaymentCard, 'id'>>
): Promise<PaymentCard> {
  const supabase = createClient()

  const updateData: any = {}
  if (updates.nickname !== undefined) updateData.nickname = updates.nickname
  if (updates.cardType !== undefined) updateData.card_type = updates.cardType
  if (updates.lastFour !== undefined) updateData.last_four = updates.lastFour
  if (updates.icon !== undefined) updateData.icon = updates.icon
  if (updates.color !== undefined) updateData.color = updates.color
  if (updates.subAccountId !== undefined) updateData.sub_account_id = updates.subAccountId

  const { data, error } = await supabase
    .from('user_payment_cards')
    .update(updateData)
    .eq('id', cardId)
    .select()
    .single()

  if (error) {
    console.error('Error updating payment card:', error)
    throw new Error('Failed to update payment card')
  }

  return {
    id: data.id,
    nickname: data.nickname,
    cardType: data.card_type as 'Visa' | 'Mastercard' | 'Amex' | 'Discover' | 'Other',
    lastFour: data.last_four,
    icon: data.icon,
    color: data.color,
    subAccountId: (data as any).sub_account_id,
  }
}

/**
 * Delete a payment card
 */
export async function deleteCard(cardId: string): Promise<void> {
  const supabase = createClient()

  const { error } = await supabase
    .from('user_payment_cards')
    .delete()
    .eq('id', cardId)

  if (error) {
    console.error('Error deleting payment card:', error)
    throw new Error('Failed to delete payment card')
  }
}
